import { Link } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { BackofficeBusinessDetailed, BusinessAddress, BusinessContactInfo } from '@/types'
import { Briefcase, Building2, Users, Mail, Globe, Tag, PieChart } from 'lucide-react'


function EmptyRow({ message }: { message: string }) {
  return (
    <div className="px-4 py-6 text-center text-sm text-muted-foreground">{message}</div>
  )
}

type Props = {
  business: BackofficeBusinessDetailed
}

export default function BackofficeBusinessesShow({ business }: Props) {
  return (
    <BackofficeLayout
      title={business.name}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Businesses', href: '/backoffice/businesses' },
        { label: business.name },
      ]}
    >
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">{business.name}</h1>
          </div>
          <p className="text-sm text-muted-foreground font-mono">{business.email}</p>
          <p className="text-xs text-muted-foreground mt-1">Account: {business.account_name}</p>
        </div>

        {/* Country Operations */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              Country Operations
            </h2>
          </div>
          <div className="px-4">
            {business.country_operations?.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 py-3">
                {business.country_operations.map(co => (
                  <Badge key={co.id} variant="secondary" className="font-normal text-xs">{co.country_name}</Badge>
                ))}
              </div>
            ) : (
              <EmptyRow message="No operating countries" />
            )}
          </div>
        </section>

        {/* Activities */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Tag className="w-4 h-4 text-muted-foreground" />
              Business Activities
            </h2>
          </div>
          <div className="px-4">
            {business.activities?.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 py-3">
                {business.activities.map(act => (
                  <Badge key={act.id} variant="secondary" className="font-normal text-xs">{act.name}</Badge>
                ))}
              </div>
            ) : (
              <EmptyRow message="No business activities" />
            )}
          </div>
        </section>

        {/* Cap Table Summary */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <PieChart className="w-4 h-4 text-muted-foreground" />
              Cap Table Summary
            </h2>
          </div>
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Shareholders</span>
              <span className="text-sm text-foreground">{business.cap_table_shareholders_count}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Shares Outstanding</span>
              <span className="text-sm text-foreground">{business.cap_table_shares_outstanding.toLocaleString()}</span>
            </div>
          </div>
        </section>

        {/* Officers */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              Officers
            </h2>
            <Link
              href={`/backoffice/businesses/${business.id}/officer_assignments`}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Manage Officers →
            </Link>
          </div>
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Compliance</span>
              <span className="text-sm text-foreground">{business.compliance_officer_email || 'Not assigned'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Support</span>
              <span className="text-sm text-foreground">{business.support_officer_email || 'Not assigned'}</span>
            </div>
          </div>
        </section>

        {/* Addresses */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Addresses</h2>
          </div>
          <div className="px-4">
            {business.addresses?.length > 0 ? (
              <div className="divide-y">
                {business.addresses.map((addr: BusinessAddress) => (
                  <div key={addr.id} className="py-3">
                    <div className="min-w-0">
                      {addr.label && <div className="text-xs text-muted-foreground mb-0.5">{addr.label}</div>}
                      <div className="text-sm font-medium text-foreground">{addr.street_address_1}</div>
                      {addr.street_address_2 && <div className="text-xs text-muted-foreground">{addr.street_address_2}</div>}
                      <div className="text-xs text-muted-foreground">{addr.city}{addr.state_province && `, ${addr.state_province}`} {addr.postal_code} · {addr.country_code}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyRow message="No addresses" />
            )}
          </div>
        </section>

        {/* Contact Info */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Contact Information</h2>
          </div>
          <div className="px-4">
            {business.contact_infos?.length > 0 ? (
              <div className="divide-y">
                {business.contact_infos.map((ci: BusinessContactInfo) => (
                  <div key={ci.id} className="py-3">
                    <div className="min-w-0">
                      {ci.label && <div className="text-xs text-muted-foreground mb-0.5">{ci.label}</div>}
                      <div className="text-sm font-medium text-foreground">{ci.email}</div>
                      {ci.phone && <div className="text-xs text-muted-foreground">{ci.phone}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyRow message="No contact information" />
            )}
          </div>
        </section>

        {/* Inbound Emails */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              Inbound Emails
            </h2>
          </div>
          <Link
            href={`/backoffice/businesses/${business.id}/inbound_emails`}
            className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
          >
            <span className="text-sm text-foreground">
              {business.inbound_emails_count > 0
                ? `${business.inbound_emails_count} email${business.inbound_emails_count === 1 ? '' : 's'}`
                : 'No emails received yet'}
            </span>
            <span className="text-xs text-muted-foreground">View all →</span>
          </Link>
        </section>

        {/* Formation */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              Formation
            </h2>
          </div>
          {business.formation ? (
            <Link
              href={`/backoffice/formations/${business.formation.id}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
            >
              <div>
                <div className="text-sm font-medium text-foreground">{business.formation.legal_name}</div>
                {business.formation.entity_name && (
                  <div className="text-xs text-muted-foreground">{business.formation.entity_name}</div>
                )}
              </div>
              <StageBadge stage={business.formation.stage} variant="formation" />
            </Link>
          ) : (
            <EmptyRow message="No formation" />
          )}
        </section>

        {/* Nominee Directors */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Nominee Directors</h2>
          </div>
          {business.nominee_directors.length > 0 ? (
            <div>
              {business.nominee_directors.map((nd, i) => (
                <Link
                  key={nd.id}
                  href={`/backoffice/nominee_directors/${nd.id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground">{nd.type_name}</span>
                  </div>
                  <StageBadge stage={nd.stage} variant="service" />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyRow message="No nominee directors" />
          )}
        </section>

        {/* Nominee Shareholders */}
        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Nominee Shareholders</h2>
          </div>
          {business.nominee_shareholders.length > 0 ? (
            <div>
              {business.nominee_shareholders.map((ns, i) => (
                <Link
                  key={ns.id}
                  href={`/backoffice/nominee_shareholders/${ns.id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground">{ns.type_name}</span>
                  </div>
                  <StageBadge stage={ns.stage} variant="service" />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyRow message="No nominee shareholders" />
          )}
        </section>

      </div>
    </BackofficeLayout>
  )
}
